import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut as fbSignOut } from 'firebase/auth';
import { auth, requestNotificationPermissionAndSaveToken } from '../shared/firebase';
import { fetchUserProfile, fetchRestaurantForUser } from '../shared/services/userService';

const AuthContext = createContext(null);

const RESTAURANT_ROLES = new Set(['restaurant', 'restaurant_owner', 'restaurantOwner', 'owner', 'admin']);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleError, setRoleError] = useState(null);

  const loadProfile = useCallback(async (fbUser) => {
    const profile = await fetchUserProfile(fbUser.uid);
    setUserProfile(profile);
    const roleOk = profile && (RESTAURANT_ROLES.has(profile.role) || profile.restaurantId);
    if (!roleOk) {
      setRoleError('This account is not registered as a restaurant. Please sign in with a restaurant account.');
      setRestaurant(null);
      return;
    }
    setRoleError(null);
    const r = await fetchRestaurantForUser({ ...profile, uid: fbUser.uid });
    setRestaurant(r);
    requestNotificationPermissionAndSaveToken(fbUser).catch(() => {});
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (fbUser) => {
      setUser(fbUser);
      if (fbUser) {
        try { await loadProfile(fbUser); }
        catch (e) { console.error('profile load', e); setRoleError(e.message); }
      } else {
        setUserProfile(null);
        setRestaurant(null);
        setRoleError(null);
      }
      setLoading(false);
    });
  }, [loadProfile]);

  const signIn = (email, password) => signInWithEmailAndPassword(auth, email, password);
  const signOut = () => fbSignOut(auth);
  const refreshRestaurant = async () => {
    if (user) {
      const r = await fetchRestaurantForUser({ ...userProfile, uid: user.uid });
      setRestaurant(r);
    }
  };

  return (
    <AuthContext.Provider value={{
      user, userProfile, restaurant, loading, roleError,
      signIn, signOut, refreshRestaurant,
      restaurantId: restaurant?.id || userProfile?.restaurantId || null,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
